import { useState, useEffect } from "react";
import Footer from "./Footer";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SidebarIcons from "./SidebarIcons";
import dayjs from "dayjs";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { GET_PROPERTY_INSPECTION_HISTORY } from "../api/apiPath";
import http from "../api/server";
import Toaster from "./common/Toaster";
import dashlogo from "../assets/img/dashbarlogo.png";

import StatusPill from "./common/StatusPill";
import useGetClientId from "../hooks/useGetClientId";

export default function PropertyInspectionsHistory() {
  const { propertyMasterId } = useParams();
  const [hideSidebar, setHideSidebar] = useState(false);
  const { userObj } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const clientObj = useGetClientId();
  const clientId = clientObj?.clientId;
  const [summary, setSummary] = useState({});
  const [inspectionRefNo, setInspectionRefNo] = useState("");
  const [groupedSteps, setGroupedSteps] = useState({});
  const [openRefNo, setOpenRefNo] = useState([]);
  const [loading, setLoading] = useState(false);

  const [toastData, setToastData] = useState({
    toastShow: false,
    toastType: "",
    toastString: "",
  });
  const closeButton = () => {
    setToastData({
      toastShow: false,
      toastData: "",
      toastType: "",
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!clientId || !propertyMasterId) return;
    const fetchBookingHistory = async () => {
      try {
        setLoading(true);

        const { data } = await http.get(GET_PROPERTY_INSPECTION_HISTORY, {
          params: {
            propertyMasterId: propertyMasterId,
            clientId: clientId,
          },
        });
        if (data?.message?.toLowerCase()?.includes("no data found")) {
          navigate(-1);
          return;
        }
        const firstEvent = data?.inspectionHistories?.[0];

        if (firstEvent?.InspectionReferenceNumber) {
          setInspectionRefNo(firstEvent.InspectionReferenceNumber);
        }

        setSummary(data?.summary?.[0] || {});

        const getIcon = (eventType) => {
          switch (eventType) {
            case "Property Onboarded":
            case "Inspection Created":
            case "Inspection Scheduled":
            case "Door Inspected":
            case "Approved":
            case "Request for Change":
              return "fa-solid fa-circle-check";

            default:
              return "fa-solid fa-circle-check";
          }
        };

        const grouped = data?.inspectionHistories?.reduce((acc, item) => {
          const refNo = item.InspectionReferenceNumber;

          if (!acc[refNo]) acc[refNo] = [];

          acc[refNo].push({
            icon: getIcon(item.EventType),
            title: item.EventType,
            date: item.EventDate,
            boxText: item.PerformedBy
              ? `${item.EventType} by - ${item.PerformedBy}`
              : item.EventType,
          });

          return acc;
        }, {});

        setGroupedSteps(grouped || {});
      } catch (err) {
        const status = err?.status ?? err?.response?.status ?? 0;

        const message = "No inspection found. Please create an inspection.";
        // err?.data?.message ?? err?.response?.data?.message ?? "No data found";

        if (status === 404 || message.toLowerCase().includes("no data found")) {
          // show toaster
          setToastData({
            toastShow: true,
            toastType: "failure",
            toastString: message,
          });

          setTimeout(() => {
            navigate(-1);
          }, 4000);

          return;
        }

        setToastData({
          toastShow: true,
          toastType: "failure",
          toastString: message || "Something went wrong",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBookingHistory();
  }, [clientId, propertyMasterId]);

  return (
    <>
      <Header hideSidebar={hideSidebar} setHideSidebar={setHideSidebar} />

      {userObj && (
        <div className="dashboard_body_main">
          <Toaster
            show={toastData.toastShow}
            toastType={toastData.toastType}
            title={toastData.toastString}
            theme={"dark"}
            position="top-center"
            closeButton={closeButton}
          />
          {hideSidebar ? (
            <Sidebar
              setHideSidebar={setHideSidebar}
              hideSidebar={hideSidebar}
            />
          ) : (
            <SidebarIcons
              setHideSidebar={setHideSidebar}
              hideSidebar={hideSidebar}
            />
          )}

          <div className="d-flex dashboard_body flex-column py-1  w-100 mt-2">
            <div className="dashboard_header">
              <div>
                <h2 className="mb-0">View Inspection History</h2>
                <p className="header-description mb-0">
                  Track the complete timeline of your site inspection
                </p>
              </div>
            </div>

            <div
              className="card mt-4 p-3 rounded-4 mb-4 orange-box d-flex gap-3 flex-row align-items-center justify-content-between"
              style={{ height: "9.5rem" }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-evenly text-center py-4">
                  <div>
                    <h1 className="mb-0" style={{ fontSize: "4rem" }}>
                      {summary?.TotalDoorsInspected}
                    </h1>
                    <p className="fw-semibold mb-0">Total Doors Inspected</p>
                  </div>
                  <div className="col-6 col-sm-4 col-lg-2 mb-1">
                    <img src={dashlogo} alt="Inspector" height="100" />
                  </div>
                  <div>
                    <h1 className="mb-0" style={{ fontSize: "4rem" }}>
                      {summary?.TotalInspections}
                    </h1>
                    <p className="fw-semibold mb-0">Total Inspections</p>
                  </div>
                  <div>
                    <h1 className="mb-0" style={{ fontSize: "4rem" }}>
                      {summary?.TotalCriticalIssues}
                    </h1>
                    <p className="fw-semibold mb-0">Total Critical Issues</p>
                  </div>
                  <div>
                    <h1 className="mb-0" style={{ fontSize: "4rem" }}>
                      {summary?.TotalNonCompliantIssues}
                    </h1>
                    <p className="fw-semibold mb-0">
                      Total Non-Compliant Issues
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="resident-card mb-3">
              <div className=" flex-sm-row align-items-center ">
                <div className="col-12 col-sm-4 col-lg-4 justify-content-sm-start w-100">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h5 className="justifyStart fw-semibold mb-0 ">
                      {summary?.PropertyName}
                    </h5>

                    <p className="justifyCenter mb-0 mt-1">
                      <StatusPill status={summary?.LatestInspectionStatus} />
                    </p>
                  </div>

                  <p className="justifyStart text mb-0 mt-1">
                    <b>Location : </b>
                    {summary?.PropertyLocation}
                  </p>

                  <div className="d-flex justify-content-between align-items-center w-100 mt-4 mb-4 text-black">
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ color: "#525254" }}>
                        Inspection Managed By :
                      </span>
                      <span>{summary?.InspectionManagedBy}</span>
                      {console.log(inspectionRefNo)}
                      {console.log(loading)}
                    </div>
                  </div>
                </div>
                <div></div>
              </div>
            </div>

            <div className="resident-card mb-3">
              <div className=" flex-sm-row align-items-center ">
                <div className="col-12 col-sm-4 col-lg-4 justify-content-sm-start w-100">
                  <h4 className="text-start fw-bold">Inspection Timeline</h4>
                  <div className="mt-3 ">
                    {Object.keys(groupedSteps).length === 0 ? (
                      <p>No history found.</p>
                    ) : (
                      Object.entries(groupedSteps).map(([refNo, events]) => (
                        <div key={refNo} className="mb-3">
                          <div
                            className="inspection-box p-3 "
                            style={{
                              // background: "#F9F9F9",
                              border: "2px solid #E5E5E5",
                              cursor: "pointer",
                              borderRadius: "8px",
                            }}
                            onClick={() => {
                              setOpenRefNo((prev) =>
                                prev.includes(refNo)
                                  ? prev.filter((item) => item !== refNo)
                                  : [...prev, refNo],
                              );
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center ">
                              <p className="fw-semibold">
                                {" "}
                                Inspection Ref No : {refNo}
                              </p>

                              <i
                                className={`bi ${
                                  openRefNo === refNo
                                    ? "bi-chevron-up"
                                    : "bi-chevron-down"
                                }`}
                              ></i>
                            </div>

                            {openRefNo.includes(refNo) && (
                              <div className="timeline mt-3">
                                {events.map((step, i) => (
                                  <div key={i} className="timeline-item-row">
                                    {/* LEFT SIDE ICON + LINE */}
                                    <div className="timeline-left">
                                      <div className="timeline-icon">
                                        <div className="exact-check-icon">
                                          <i className="fa-solid fa-check"></i>
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      className="inspection-box text-start p-3"
                                      style={{
                                        backgroundColor: "#F9F9F9",
                                        border: "1px solid #E5E5E5",
                                      }}
                                    >
                                      <div>{step.boxText}</div>

                                      <div
                                        className="inspection-date text-muted"
                                        style={{ fontSize: "13px" }}
                                      >
                                        {/* {step.date} */}
                                        {dayjs(step.date, "DD-MMM-YYYY hh:mm A")
                                          .format("DD-MMM-YYYY hh:mm A")
                                          .toUpperCase()}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="card-body action_btns d-flex justify-content-end gap-3 pb-3 px-3 flex-wrap flex-sm-nowrap">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        handleBack();
                      }}
                    >
                      Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      )}
    </>
  );
}
